import { trapFocus, removeTrapFocus, cycleFocus } from '@theme/focus';

/**
 * gb-header — comportamento della barra fluttuante e dei pannelli.
 * Vedi sections/gb-header.liquid per il contratto con il tema Horizon.
 */

const DESKTOP = '(min-width: 1280px)';

/* ───────────────────────────────────────────────────────────────────────────
   Barra: aggancio al bordo superiore allo scroll verso il basso.
   ─────────────────────────────────────────────────────────────────────────── */

class GbHeaderBar extends HTMLElement {
  /** Travel accumulato nella stessa direzione prima di cambiare stato. */
  static THRESHOLD = 8;

  #lastY = 0;
  #accum = 0;
  #raf = null;
  #docked = false;
  #frozen = false;
  /** Punti di soglia in coordinate documento; null = da ricalcolare. */
  #points = null;
  #footerEl = null;

  connectedCallback() {
    this.#lastY = Math.max(0, window.scrollY);
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#invalidate, { passive: true });
    // L'altezza dell'hero cambia quando immagini e video finiscono di caricare.
    window.addEventListener('load', this.#invalidate, { once: true });
    // Il browser può ripristinare una posizione di scroll già sul footer.
    this.#checkFooter();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#invalidate);
    if (this.#raf !== null) cancelAnimationFrame(this.#raf);
  }

  #invalidate = () => {
    this.#points = null;
  };

  /**
   * `dock`: oltre questo punto lo scroll verso il basso aggancia la barra.
   * `undock`: risalendo, la barra torna fluttuante solo una volta rientrati qui.
   * Sulle pagine con hero il punto di ritorno è il fondo dell'hero, così la
   * barra resta agganciata per tutta la pagina e si stacca quando si rivede
   * l'immagine di apertura; altrove coincide con l'offset di riposo.
   */
  #thresholds() {
    if (this.#points) return this.#points;

    const styles = getComputedStyle(this);
    const dock = parseInt(styles.top, 10) || 0;
    const dockedTop = parseInt(styles.getPropertyValue('--gb-bar-docked-top'), 10) || 0;

    const hero = document.querySelector(
      'main.content-for-layout .gb-hero, main.content-for-layout .gbc-hero'
    );
    let undock = dock;
    if (hero) {
      const rect = hero.getBoundingClientRect();
      undock = Math.max(dock, Math.round(rect.top + window.scrollY + rect.height));
    }

    // Bordo inferiore della barra quando è agganciata: oltre questo punto il
    // footer la sta raggiungendo.
    const barBottom = dockedTop + this.offsetHeight;

    this.#points = { dock, undock, barBottom };
    return this.#points;
  }

  /** Con un pannello aperto la barra resta ferma e visibile, come sul sito WP. */
  set frozen(value) {
    this.#frozen = value;
    if (value) {
      this.removeAttribute('behind-footer');
      this.#undock();
    }
  }

  #onScroll = () => {
    if (this.#raf !== null) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = null;
      this.#measure();
    });
  };

  #measure() {
    const y = Math.max(0, window.scrollY);
    const delta = y - this.#lastY;
    this.#lastY = y;
    if (this.#frozen) return;

    this.#checkFooter();
    if (delta === 0) return;

    // L'isteresi accumula il movimento nella stessa direzione e si azzera a ogni
    // inversione: una soglia calcolata sul singolo frame non scatterebbe mai
    // durante uno scroll lento da trackpad.
    if (delta > 0 !== this.#accum > 0) this.#accum = 0;
    this.#accum += delta;
    if (Math.abs(this.#accum) < GbHeaderBar.THRESHOLD) return;

    const { dock, undock } = this.#thresholds();
    const next = this.#accum > 0 ? y > dock : y > undock;
    if (next === this.#docked) return;
    this.#docked = next;
    this.toggleAttribute('docked', next);
  }

  /**
   * Arrivati in fondo, la barra si ritira dietro il footer. Il footer ha già uno
   * z-index superiore (vedi assets/gb-header.css) e la copre dove i due si
   * sovrappongono; questo la toglie del tutto quando il footer è più basso della
   * finestra e non arriverebbe a coprirla per intero.
   */
  #checkFooter() {
    const footer = (this.#footerEl ??= document.querySelector('body > footer'));
    if (!footer || !window.matchMedia(DESKTOP).matches) {
      this.removeAttribute('behind-footer');
      return;
    }
    const { barBottom } = this.#thresholds();
    this.toggleAttribute('behind-footer', footer.getBoundingClientRect().top <= barBottom);
  }

  #undock() {
    this.#docked = false;
    this.#accum = 0;
    this.removeAttribute('docked');
  }
}

customElements.define('gb-header-bar', GbHeaderBar);

/* ───────────────────────────────────────────────────────────────────────────
   Pannelli, dropdown, drawer.
   ─────────────────────────────────────────────────────────────────────────── */

class GbHeaderController {
  /** @type {{ surface: HTMLElement, trigger: HTMLElement } | null} */
  #open = null;
  /** @type {HTMLElement | null} */
  #openDropdown = null;
  #dropdownTimer = null;

  constructor(root) {
    this.root = root;
    this.bar = root.querySelector('gb-header-bar');
    this.spacer = root.querySelector('.gb-header__spacer');
    this.desktop = window.matchMedia(DESKTOP);

    this.#wireBurger();
    this.#wirePanelToggles();
    this.#wireDropdowns();
    this.#wireAccordions();
    this.#wireSearch();

    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('pointerdown', this.#onPointerDown);
    this.desktop.addEventListener('change', this.#onBreakpointChange);

    this.#observeHeight();
  }

  /* ── superfici (pannelli e drawer) ── */

  #surfaceFor(trigger) {
    // L'hamburger apre il pannello desktop o il drawer a seconda del breakpoint.
    const id = trigger.dataset.gbPanelToggle ?? (this.desktop.matches ? 'gb-nav-main' : 'gb-drawer');
    return this.root.querySelector(`#${id}`);
  }

  #openSurface(surface, trigger) {
    if (this.#open) this.#closeSurface({ restoreFocus: false });
    this.#closeDropdown();

    surface.removeAttribute('inert');
    surface.setAttribute('open', '');
    trigger.setAttribute('aria-expanded', 'true');
    if (trigger.hasAttribute('aria-label')) trigger.setAttribute('aria-label', 'Chiudi il menu');

    // Il blocco dello scroll serve solo al drawer, che copre l'intera finestra:
    // sui pannelli desktop il sito WP lascia la pagina scorrevole.
    if (surface.matches('gb-header-drawer')) {
      document.documentElement.setAttribute('scroll-lock', '');
    }

    if (this.bar) this.bar.frozen = true;
    this.#open = { surface, trigger };

    // Il focus è confinato all'header nel suo insieme: le superfici chiuse sono
    // `inert`, quindi il ciclo tocca solo la barra e la superficie aperta.
    trapFocus(this.root);
    trigger.focus();
  }

  #closeSurface({ restoreFocus = true } = {}) {
    if (!this.#open) return;
    const { surface, trigger } = this.#open;
    this.#open = null;

    // Il focus deve uscire prima che la superficie diventi inert.
    if (restoreFocus || surface.contains(document.activeElement)) trigger.focus();

    surface.removeAttribute('open');
    surface.setAttribute('inert', '');
    trigger.setAttribute('aria-expanded', 'false');
    if (trigger.hasAttribute('aria-label')) trigger.setAttribute('aria-label', 'Apri il menu');

    document.documentElement.removeAttribute('scroll-lock');
    removeTrapFocus();
    if (this.bar) this.bar.frozen = false;
  }

  #toggleSurface(trigger) {
    const surface = this.#surfaceFor(trigger);
    if (!surface) return;
    if (this.#open?.surface === surface) this.#closeSurface();
    else this.#openSurface(surface, trigger);
  }

  #wireBurger() {
    const burger = this.root.querySelector('[data-gb-menu-toggle]');
    if (!burger) return;
    this.burger = burger;
    this.#syncBurgerTarget();
    burger.addEventListener('click', () => this.#toggleSurface(burger));
  }

  #syncBurgerTarget() {
    if (!this.burger) return;
    this.burger.setAttribute('aria-controls', this.desktop.matches ? 'gb-nav-main' : 'gb-drawer');
  }

  #wirePanelToggles() {
    for (const trigger of this.root.querySelectorAll('[data-gb-panel-toggle]')) {
      trigger.addEventListener('click', () => this.#toggleSurface(trigger));
    }
  }

  /* ── dropdown ── */

  #wireDropdowns() {
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)');

    for (const dd of this.root.querySelectorAll('[data-gb-dd]')) {
      const toggle = dd.querySelector('[data-gb-dd-toggle]');
      const menu = dd.querySelector('[data-gb-dd-menu]');
      if (!toggle || !menu) continue;

      toggle.addEventListener('click', () => {
        if (this.#openDropdown === dd) this.#closeDropdown();
        else this.#showDropdown(dd);
      });

      toggle.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        this.#showDropdown(dd);
        menu.querySelector('a')?.focus();
      });

      menu.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        event.preventDefault();
        cycleFocus(Array.from(menu.querySelectorAll('a')), event.key === 'ArrowDown' ? 1 : -1);
      });

      // Il puntatore fine apre in hover; la chiusura è ritardata per non perdere
      // il menu mentre il mouse lo raggiunge in diagonale.
      if (fine.matches) {
        dd.addEventListener('pointerenter', () => {
          clearTimeout(this.#dropdownTimer);
          this.#showDropdown(dd);
        });
        dd.addEventListener('pointerleave', () => {
          clearTimeout(this.#dropdownTimer);
          this.#dropdownTimer = setTimeout(() => this.#closeDropdown(), 150);
        });
      }

      dd.addEventListener('focusout', (event) => {
        if (!dd.contains(event.relatedTarget)) this.#closeDropdown();
      });
    }
  }

  #showDropdown(dd) {
    if (this.#openDropdown && this.#openDropdown !== dd) this.#closeDropdown();
    dd.querySelector('[data-gb-dd-menu]')?.removeAttribute('hidden');
    dd.querySelector('[data-gb-dd-toggle]')?.setAttribute('aria-expanded', 'true');
    this.#openDropdown = dd;
  }

  #closeDropdown({ restoreFocus = false } = {}) {
    const dd = this.#openDropdown;
    if (!dd) return;
    this.#openDropdown = null;
    dd.querySelector('[data-gb-dd-menu]')?.setAttribute('hidden', '');
    const toggle = dd.querySelector('[data-gb-dd-toggle]');
    toggle?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) toggle?.focus();
  }

  /* ── accordion del drawer ── */

  #wireAccordions() {
    for (const toggle of this.root.querySelectorAll('.gb-acc__toggle')) {
      const panel = toggle.nextElementSibling;
      if (!panel) continue;
      toggle.addEventListener('click', () => {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!expanded));
        panel.toggleAttribute('hidden', expanded);
      });
    }
  }

  /* ── ricerca: riusa la dialog globale di layout/theme.liquid ── */

  #wireSearch() {
    const button = this.root.querySelector('[data-gb-search]');
    button?.addEventListener('click', () => {
      this.#closeSurface({ restoreFocus: false });
      document.getElementById('search-modal')?.showDialog?.();
    });
  }

  /* ── eventi globali ── */

  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (this.#openDropdown) this.#closeDropdown({ restoreFocus: true });
    else if (this.#open) this.#closeSurface();
  };

  #onPointerDown = (event) => {
    if (this.root.contains(event.target)) return;
    this.#closeDropdown();
    this.#closeSurface({ restoreFocus: false });
  };

  #onBreakpointChange = () => {
    // Attraversare il breakpoint con una superficie aperta lascerebbe visibile
    // un pannello che a quel punto è nascosto da CSS.
    this.#closeSurface({ restoreFocus: false });
    this.#closeDropdown();
    this.#syncBurgerTarget();
  };

  /* ── variabili CSS del contratto Horizon ── */

  #observeHeight() {
    if (!this.spacer) return;
    // assets/header.js manteneva queste variabili al resize; non caricandolo più,
    // il compito passa qui (lo script inline di theme.liquid le imposta solo al load).
    const sync = () => {
      const height = `${Math.round(this.root.offsetHeight)}px`;
      document.body.style.setProperty('--header-height', height);
      document.body.style.setProperty('--header-group-height', height);
    };
    new ResizeObserver(sync).observe(this.spacer);
    sync();
  }
}

const root = document.getElementById('header-component');
if (root) new GbHeaderController(root);
